import React, { useEffect, useRef } from "react";
import Chart from "chart.js/auto";

export const CHART_PALETTE = ["#5c6ac4", "#007ace", "#47c1bf", "#9c6ade", "#f49342", "#919eab"];
const AXIS = "#6d7175";
const GRID = "rgba(0,0,0,0.06)";

/* Grouped bar chart: switches vs views per day. */
export function BarChart({ labels, switches, views, height = 300 }) {
    const canvasRef = useRef(null);
    const chartRef = useRef(null);
    const key = JSON.stringify({ labels, switches, views });

    useEffect(() => {
        if (!canvasRef.current) return undefined;
        chartRef.current = new Chart(canvasRef.current.getContext("2d"), {
            type: "bar",
            data: {
                labels,
                datasets: [
                    { label: "Switches", data: switches, backgroundColor: CHART_PALETTE[0], borderRadius: 5, maxBarThickness: 16 },
                    { label: "Views", data: views, backgroundColor: CHART_PALETTE[2], borderRadius: 5, maxBarThickness: 16 },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                interaction: { mode: "index", intersect: false },
                plugins: {
                    legend: {
                        position: "top",
                        align: "end",
                        labels: { usePointStyle: true, pointStyle: "circle", boxWidth: 8, boxHeight: 8, padding: 16, font: { size: 12 }, color: AXIS },
                    },
                    tooltip: {
                        backgroundColor: "#1a1a1a",
                        padding: 10,
                        cornerRadius: 8,
                        titleFont: { size: 12 },
                        bodyFont: { size: 12 },
                        usePointStyle: true,
                    },
                },
                scales: {
                    x: { stacked: false, grid: { display: false }, ticks: { autoSkip: true, maxTicksLimit: 12, font: { size: 11 }, color: AXIS } },
                    y: { beginAtZero: true, ticks: { precision: 0, font: { size: 11 }, color: AXIS }, grid: { color: GRID, drawBorder: false } },
                },
            },
        });
        return () => {
            if (chartRef.current) chartRef.current.destroy();
        };
    }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div style={{ height, position: "relative" }}>
            <canvas ref={canvasRef} />
        </div>
    );
}

/* Doughnut chart for the currency distribution. */
export function DoughnutChart({ labels, data, height = 240 }) {
    const canvasRef = useRef(null);
    const chartRef = useRef(null);
    const key = JSON.stringify({ labels, data });

    useEffect(() => {
        if (!canvasRef.current) return undefined;
        chartRef.current = new Chart(canvasRef.current.getContext("2d"), {
            type: "doughnut",
            data: {
                labels,
                datasets: [{ data, backgroundColor: CHART_PALETTE, borderWidth: 2, borderColor: "#ffffff", hoverOffset: 6 }],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                cutout: "62%",
                plugins: {
                    legend: {
                        position: "right",
                        labels: { usePointStyle: true, pointStyle: "circle", boxWidth: 8, boxHeight: 8, padding: 12, font: { size: 12 }, color: AXIS },
                    },
                    tooltip: {
                        backgroundColor: "#1a1a1a",
                        padding: 10,
                        cornerRadius: 8,
                        callbacks: {
                            label: (ctx) => {
                                const total = ctx.dataset.data.reduce((s, v) => s + v, 0) || 1;
                                const pct = Math.round((ctx.parsed / total) * 1000) / 10;
                                return ` ${ctx.label}: ${ctx.parsed.toLocaleString()} (${pct}%)`;
                            },
                        },
                    },
                },
            },
        });
        return () => {
            if (chartRef.current) chartRef.current.destroy();
        };
    }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div style={{ height, position: "relative" }}>
            <canvas ref={canvasRef} />
        </div>
    );
}
